import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function CreditAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Report - Credit ratings API" description="Query report data via the Credit ratings API; request parameters, code examples and the response schema for the report endpoint."  canonicalUrl="https://axionquant.com/docs/04-macros-and-signals/credit-ratings/report" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Report - Credit ratings API</DocHeading>

      <div className="space-y-12" ref={sectionRef}>
        {/* Get Credit Ratings */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /credit/ratings/:id
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all credit ratings for a specific organization identified by its ID. Returns detailed rating information including rating type, current outlook, and review dates. Essential for analyzing creditworthiness and monitoring rating changes over time.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">id</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Organization ID obtained from the search endpoint
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <Links links={['/credit/ratings/12345', '/credit/ratings/67890']} />
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">orgDebtTypeDesc <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Description of debt type (e.g., &quot;Issuer Credit Rating&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">rating <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Credit rating letter grade (e.g., &quot;B&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ratingDate <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Date when rating was assigned (DD-MMM-YYYY)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">currentCwOl <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Current credit watch/outlook (e.g., &quot;Stable&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">currentCwOlDate <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Date of current outlook (DD-MMM-YYYY)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ratingDebtTypeCode <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Rating debt type code (e.g., &quot;ICR&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">lastReviewDate <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Last review date (DD-MMM-YYYY)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">coreOrgId <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Core organization ID</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">endorsementIndicator <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Regulatory endorsement indicator</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">coreRatingId <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Unique rating identifier</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ratingTypeCode <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Rating type code (e.g., &quot;STDLONG&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ratingTypeCodeDesc <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Rating type description</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">pcrFlag <span className="text-gray-500">boolean</span></p>
                  <p className="text-sm text-gray-600">Public Credit Rating flag</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">id <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Sequential identifier</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Credit Ratings" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Credit Ratings</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="credit/ratings"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="04-macros-and-signals/credit-ratings/report"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
